import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import TopBar from '../components/TopBar';
import ConciergeLauncher from '../components/ConciergeLauncher';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '../components/ui/breadcrumb';
import { CheckCircle2, Circle, ArrowRight } from 'lucide-react';

const PathwayDetailPage = () => {
  const { slug } = useParams();
  const { pathways, pathwayProgress, updatePathwayProgress, experts } = useData();
  const navigate = useNavigate();
  const pathway = pathways.find(p => p.slug === slug);

  if (!pathway) return <div>Pathway not found</div>;

  const progress = pathwayProgress[pathway.id] || { completedSteps: [] };
  const completedCount = progress.completedSteps.length;
  const percent = Math.round((completedCount / pathway.steps.length) * 100);

  const getStepStatus = (step) => {
    if (progress.completedSteps.includes(step.id)) return 'DONE';
    if (completedCount === pathway.steps.indexOf(step)) return 'NEXT';
    return 'TODO';
  };

  const pillarColors = {
    LEARN: 'hsl(var(--violet))',
    KNOW: 'hsl(var(--blue))',
    CONNECT: 'hsl(var(--mint))',
    ACT: 'hsl(var(--primary))'
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'hsl(var(--bg))' }}>
      <TopBar />

      <div className="wrap section">
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem><BreadcrumbLink href="/">Home</BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbPage>{pathway.title}</BreadcrumbPage></BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="mb-8">
          <h1 className="mb-3">{pathway.title}</h1>
          <p className="text-lg" style={{ color: 'hsl(var(--ink-muted))' }}>{pathway.summary}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2 space-y-6">
            {/* Progress Card */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-3">
                <h3>Your Progress</h3>
                <span className="text-sm font-semibold" style={{ color: 'hsl(var(--primary))' }}>{percent}%</span>
              </div>
              <div className="w-full h-2 rounded-full mb-2" style={{ backgroundColor: 'hsl(var(--line-2))' }}>
                <div className="h-full rounded-full" style={{ width: `${percent}%`, backgroundColor: 'hsl(var(--mint))' }} />
              </div>
              <p className="text-sm" style={{ color: 'hsl(var(--ink-muted))' }}>
                {completedCount} of {pathway.steps.length} steps complete
              </p>
            </Card>

            {/* Stepper */}
            <div className="space-y-4">
              {pathway.steps.map((step, idx) => {
                const status = getStepStatus(step);
                return (
                  <Card key={step.id} className={`p-6 ${status === 'NEXT' ? 'border-2' : ''}`} style={status === 'NEXT' ? { borderColor: 'hsl(var(--primary))' } : {}}>
                    <div className="flex items-start gap-4">
                      <div className="flex flex-col items-center flex-shrink-0">
                        {status === 'DONE' ? (
                          <CheckCircle2 className="w-6 h-6" style={{ color: 'hsl(var(--mint))' }} />
                        ) : status === 'NEXT' ? (
                          <div className="w-6 h-6 rounded-full border-2 flex items-center justify-center" style={{ borderColor: 'hsl(var(--primary))', backgroundColor: 'hsl(var(--primary))', color: 'white', fontSize: '10px', fontWeight: '700' }}>
                            {idx + 1}
                          </div>
                        ) : (
                          <Circle className="w-6 h-6" style={{ color: 'hsl(var(--line))' }} />
                        )}
                        {idx < pathway.steps.length - 1 && (
                          <div className="w-0.5 h-8 mt-2" style={{ backgroundColor: status === 'DONE' ? 'hsl(var(--mint))' : 'hsl(var(--line))' }} />
                        )}
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-semibold px-2 py-0.5 rounded" style={{ backgroundColor: 'hsl(var(--surface-2))', color: pillarColors[step.pillar] }}>
                            {step.pillar}
                          </span>
                          {status === 'NEXT' && (
                            <span className="text-xs font-semibold" style={{ color: 'hsl(var(--primary))' }}>Current step</span>
                          )}
                        </div>
                        <h4 className="mb-1">{step.title}</h4>
                        <p className="text-sm mb-3" style={{ color: 'hsl(var(--ink-muted))' }}>{step.description}</p>
                        {status !== 'DONE' && (
                          <Button size="sm" variant={status === 'NEXT' ? 'default' : 'outline'}>
                            {step.ctaLabel}
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>

          <aside className="space-y-6">
            <Card className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-sm font-semibold">✦ Grid Guide</span>
              </div>
              <p className="text-sm mb-3" style={{ color: 'hsl(var(--ink-muted))' }}>Need help with this pathway? Ask the AI concierge.</p>
              <Button size="sm" variant="outline" className="w-full" onClick={() => navigate('/ask')}>Open Grid Guide</Button>
            </Card>

            <Card className="p-5">
              <h4 className="text-sm font-semibold mb-3">Others on this pathway</h4>
              <div className="space-y-2">
                {experts.slice(0, 3).map(expert => (
                  <div key={expert.id} className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold" style={{ backgroundColor: expert.avatarColor, color: 'white' }}>
                      {expert.initials}
                    </div>
                    <div className="text-sm">{expert.name}</div>
                  </div>
                ))}
              </div>
            </Card>
          </aside>
        </div>
      </div>

      <ConciergeLauncher />
    </div>
  );
};

export default PathwayDetailPage;
